import { Link } from "@tanstack/react-router";
import { Anchor, BarLineChart, Colors, Sale03, Tag01 } from "@untitledui/icons";
import { VideoOff } from "lucide-react";
import { useState } from "react";
import { useProductContentAnalysis } from "@/api/product";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { useMerchant } from "@/providers/MerchantProvider";
import { toTitleCase } from "@/utils/toTitleCase";

type ContentAnalysisTabProps = {
  productId: string;
  productName: string;
};

export const ContentAnalysisTab = ({ productId, productName }: ContentAnalysisTabProps) => {
  const { data: contentAnalysis, isLoading } = useProductContentAnalysis(productId);
  const merchant = useMerchant();
  const [showBriefCard, setShowBriefCard] = useState(true);

  if (isLoading) {
    return (
      <div className="flex w-full h-48 justify-center items-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!contentAnalysis) {
    return (
      <div className="w-full h-[400px] bg-gray-50 rounded-lg flex flex-col items-center justify-center text-center mt-8">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: "#F5F5F5" }}
        >
          <VideoOff className="w-6 h-6" style={{ color: "#717680" }} />
        </div>
        <h4 className="text-sm font-medium text-primary mb-1">Content analysis unavailable</h4>
        <p className="text-sm text-tertiary max-w-md">
          Content will be auto analysed once creators begin <br /> publishing videos selling this product.
        </p>
      </div>
    );
  }

  // Helper to check if a category has any patterns
  const hasPatterns = (category: { do: string[]; dont: string[] }) =>
    category.do.length > 0 || category.dont.length > 0;

  // Helper to get all patterns for description text
  const getAllPatterns = (category: { do: string[]; dont: string[] }) => [...category.do, ...category.dont];

  const hasData =
    hasPatterns(contentAnalysis.videoStyle) ||
    hasPatterns(contentAnalysis.hooks) ||
    hasPatterns(contentAnalysis.sellingPoints) ||
    hasPatterns(contentAnalysis.tactics);

  if (!hasData) {
    return (
      <div className="w-full h-[400px] bg-gray-50 rounded-lg flex flex-col items-center justify-center text-center mt-8">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: "#F5F5F5" }}
        >
          <VideoOff className="w-6 h-6" style={{ color: "#717680" }} />
        </div>
        <h4 className="text-sm font-medium text-primary mb-1">Content analysis unavailable</h4>
        <p className="text-sm text-tertiary max-w-md">
          Content will be auto analysed once creators begin publishing videos selling this product.
        </p>
      </div>
    );
  }

  // Render badges with do/dont colors (green for do, orange for dont)
  const renderBadges = (category: { do: string[]; dont: string[] }, formatFn?: (s: string) => string) => (
    <div className="flex flex-wrap gap-2">
      {category.do.map((item) => (
        <Badge key={item} color="success" className="bg-success-50 text-success-700 border-success-200">
          {formatFn ? formatFn(item) : item}
        </Badge>
      ))}
      {category.dont.map((item) => (
        <Badge key={item} color="warning" className="bg-warning-50 text-warning-700 border-warning-200">
          {formatFn ? formatFn(item) : item}
        </Badge>
      ))}
    </div>
  );

  // Build sections array for separator logic
  const sections: React.ReactNode[] = [];

  if (hasPatterns(contentAnalysis.videoStyle)) {
    const allStyles = getAllPatterns(contentAnalysis.videoStyle);
    sections.push(
      <div key="video-style" className="space-y-3">
        <div className="flex items-center gap-2">
          <Colors className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">Video Style</h3>
        </div>
        <p className="text-sm text-tertiary leading-relaxed">
          Content showcasing the product through {allStyles.slice(0, 3).join(", ").toLowerCase()} themes has been
          effective in driving engagement across {contentAnalysis.totalVideos} videos.
        </p>
        {renderBadges(contentAnalysis.videoStyle)}
      </div>,
    );
  }

  if (hasPatterns(contentAnalysis.hooks)) {
    const allHooks = getAllPatterns(contentAnalysis.hooks);
    sections.push(
      <div key="hooks" className="space-y-3">
        <div className="flex items-center gap-2">
          <Anchor className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">Hook</h3>
        </div>
        <p className="text-sm text-tertiary leading-relaxed">
          Content that opened with {allHooks[0]?.toLowerCase() || "engaging hooks"} has effectively drawn viewers in and
          maintained engagement throughout the video.
        </p>
        {renderBadges(contentAnalysis.hooks)}
      </div>,
    );
  }

  if (hasPatterns(contentAnalysis.sellingPoints)) {
    const allPoints = getAllPatterns(contentAnalysis.sellingPoints);
    sections.push(
      <div key="selling-points" className="space-y-3">
        <div className="flex items-center gap-2">
          <Tag01 className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">Selling points</h3>
        </div>
        <p className="text-sm text-tertiary leading-relaxed">
          Creators highlighted {allPoints.slice(0, 3).join(", ").toLowerCase()}, emphasizing key product benefits that
          resonated with audiences.
        </p>
        {renderBadges(contentAnalysis.sellingPoints)}
      </div>,
    );
  }

  if (hasPatterns(contentAnalysis.tactics)) {
    const allTactics = getAllPatterns(contentAnalysis.tactics);
    sections.push(
      <div key="tactics" className="space-y-3">
        <div className="flex items-center gap-2">
          <Sale03 className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">Sales Tactics</h3>
        </div>
        <p className="text-sm text-tertiary leading-relaxed">
          Content showing {allTactics.slice(0, 3).join(", ").toLowerCase().replaceAll("_", " ")} demonstrated
          authenticity and drove conversions effectively.
        </p>
        {renderBadges(contentAnalysis.tactics, (s) => toTitleCase(s.replaceAll("_", " ")))}
      </div>,
    );
  }

  return (
    <div className="flex gap-14 mt-8">
      {/* Left Column */}
      <div className="w-[200px] flex-shrink-0 space-y-14">
        {/* Content Performance Section */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <BarLineChart className="w-5 h-5 text-tertiary" />
            <h3 className="text-sm font-medium text-primary">Content Performance</h3>
          </div>
          <p className="text-xs text-tertiary leading-relaxed">
            A breakdown of what is contributing to high-performing content right now
          </p>
        </div>

        {/* Generate Brief Card */}
        {showBriefCard && (
          <div className="p-3 overflow-hidden" style={{ backgroundColor: "#F9F5FF" }}>
            <img src="/mockup-content-analysis.png" alt="Content Analysis" className="w-full object-cover" />
            <div className="space-y-4 mt-3">
              <p className="text-sm text-primary leading-relaxed">
                Rapidly translate this Smart Insight into a GMV-aligned strategic brief
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowBriefCard(false)}
                  className="text-sm text-tertiary hover:text-primary transition-colors"
                >
                  Dismiss
                </button>
                <Link
                  to="/merchant/$merchantId/smart-briefs"
                  params={{ merchantId: merchant }}
                  search={{ productId, productName }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
                >
                  Generate Brief
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Column - Content Analysis Sections with Separators */}
      <div className="flex-1 min-w-0">
        {sections.map((section, index) => (
          <div key={index}>
            {section}
            {index < sections.length - 1 && <div className="my-10 border-t border-border-primary" />}
          </div>
        ))}
      </div>
    </div>
  );
};
